import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BarChart3, ChevronRight, Database, FileSpreadsheet, FileText, Globe2, Layers, Settings2, TrendingUp, Users, Wallet, Receipt } from "lucide-react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import { getMapeamentoOrigemDados } from "../../api";
import SideBarNavOrigemDados from "../../componets/SideBarNavOrigemDados";

// Mesmo mapeamento de cor usado nos badges da tela de Mapeamento —
// mantém as duas telas visualmente consistentes.
const ORIGEM_CORES = {
  "Google Sheets": "#10B981",
  "Banco de Dados": "#3B82F6",
  "API Externa": "#F59E0B",
  "Arquivo CSV": "#8B5CF6",
};
const ORIGEM_ICONES = {
  "Google Sheets": FileSpreadsheet,
  "Banco de Dados": Database,
  "API Externa": Globe2,
  "Arquivo CSV": FileText,
};
const ORIGENS = Object.keys(ORIGEM_CORES);
const COR_PADRAO = "#94A3B8";

const GESTAO_ORCAMENTARIA = "Gestão Orçamentária";

// Área = módulo sem o detalhe entre parênteses; toda a Gestão Orçamentária vira uma área só
// (são ~30 linhas no CSV, que antes viravam ~30 barras no gráfico).
function areaDoModulo(modulo) {
  if (modulo.startsWith(GESTAO_ORCAMENTARIA)) return GESTAO_ORCAMENTARIA;
  return modulo.replace(/\s*\(.*\)\s*$/, "") || "Outros";
}

// Dentro da Gestão Orçamentária: "Gestão Orçamentária / Despesas Gerais (Realizado)" →
// tela "Despesas Gerais", aspecto "Realizado".
function telaEAspecto(modulo) {
  const resto = modulo.slice(GESTAO_ORCAMENTARIA.length).replace(/^\s*\/\s*/, "");
  const m = resto.match(/^(.*?)\s*\((.*)\)\s*$/);
  return m ? { tela: m[1], aspecto: m[2] } : { tela: resto, aspecto: null };
}

// Temas da Gestão Orçamentária, na ordem em que aparecem no detalhe. A primeira regra que casar
// com o nome da tela vence; o que não casar cai em "Outros".
const TEMAS_GESTAO = [
  { tema: "Premissas", icone: Settings2, cor: "#6366F1", casa: (t) => /Índices|Premissas/.test(t) },
  { tema: "Receitas", icone: TrendingUp, cor: "#10B981", casa: (t) => /Faturamento Bruto|Novas Vendas/.test(t) },
  { tema: "Pessoas / Mão de Obra", icone: Users, cor: "#EC4899", casa: (t) => /Mão de Obra|Alocação|Kits de Admissão/.test(t) },
  { tema: "Custos e Despesas Gerais", icone: Wallet, cor: "#3B82F6", casa: (t) => /Despesas e Custos Gerais|Despesas Gerais|Custos Gerais|Projetos/.test(t) },
  { tema: "Financeiro e Fiscal", icone: Receipt, cor: "#F59E0B", casa: (t) => /Financeiras|Impostos|Investimentos/.test(t) },
  { tema: "Relatórios e DRE", icone: BarChart3, cor: "#7C3AED", casa: (t) => /DRE|Análise|Visão Executiva/.test(t) },
];
const TEMA_OUTROS = { tema: "Outros", icone: Layers, cor: COR_PADRAO };

function contarOrigens(lista) {
  const contagem = {};
  for (const item of lista) {
    const chave = item.origem_dados || "Outros";
    contagem[chave] = (contagem[chave] || 0) + 1;
  }
  return contagem;
}

function BadgesOrigem({ contagem }) {
  return (
    <div className="flex flex-wrap gap-1">
      {Object.entries(contagem).map(([origem, qtd]) => (
        <span key={origem} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
          style={{ background: `${ORIGEM_CORES[origem] || COR_PADRAO}1A`, color: ORIGEM_CORES[origem] || COR_PADRAO }}>
          {origem}{qtd > 1 ? ` · ${qtd}` : ""}
        </span>
      ))}
    </div>
  );
}

function TooltipArea({ active, payload }) {
  if (!active || !payload?.length) return null;
  const linha = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md px-3 py-2 text-xs">
      <p className="font-semibold text-gray-800 mb-1">{linha.area}</p>
      {ORIGENS.filter((o) => linha[o]).map((o) => (
        <p key={o} className="flex items-center gap-1.5 text-gray-600">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: ORIGEM_CORES[o] }} />
          {o}: <strong>{linha[o]}</strong>
        </p>
      ))}
      <p className="text-gray-400 mt-1">Clique para ver o detalhe</p>
    </div>
  );
}

// Detalhe da área selecionada. Gestão Orçamentária: agrupada por tema → tela, com os aspectos
// (o que vinha entre parênteses no CSV) como etiquetas. Demais áreas: lista simples.
function DetalheArea({ area, itens }) {
  const itensArea = itens.filter((i) => areaDoModulo(i.modulo || "") === area);

  if (area !== GESTAO_ORCAMENTARIA) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {itensArea.map((item, idx) => (
          <div key={idx} className="border border-gray-100 rounded-xl p-3">
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="text-sm font-semibold text-gray-800">{item.modulo}</p>
              <BadgesOrigem contagem={contarOrigens([item])} />
            </div>
            {item.planilha_google_sheets && item.planilha_google_sheets !== "—" && (
              <p className="text-xs text-gray-500">{item.planilha_google_sheets} · {item.aba_worksheet}</p>
            )}
            {item.observacoes && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{item.observacoes}</p>}
          </div>
        ))}
      </div>
    );
  }

  const temas = new Map();
  for (const item of itensArea) {
    const { tela, aspecto } = telaEAspecto(item.modulo);
    const tema = TEMAS_GESTAO.find((t) => t.casa(tela)) || TEMA_OUTROS;
    if (!temas.has(tema.tema)) temas.set(tema.tema, { ...tema, telas: new Map() });
    const telas = temas.get(tema.tema).telas;
    if (!telas.has(tela)) telas.set(tela, { tela, aspectos: [], itens: [] });
    const entrada = telas.get(tela);
    entrada.itens.push(item);
    if (aspecto) entrada.aspectos.push(aspecto);
  }
  const ordem = [...TEMAS_GESTAO, TEMA_OUTROS].map((t) => t.tema);
  const grupos = Array.from(temas.values()).sort((a, b) => ordem.indexOf(a.tema) - ordem.indexOf(b.tema));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {grupos.map(({ tema, icone: Icone, cor, telas }) => (
        <div key={tema} className="border border-gray-100 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: `${cor}12`, borderBottom: `1px solid ${cor}33` }}>
            <Icone className="w-4 h-4" style={{ color: cor }} />
            <span className="text-sm font-semibold text-gray-800">{tema}</span>
            <span className="ml-auto text-[11px] text-gray-500">{telas.size} {telas.size === 1 ? "tela" : "telas"}</span>
          </div>
          <ul className="divide-y divide-gray-50">
            {Array.from(telas.values()).map(({ tela, aspectos, itens: itensTela }) => (
              <li key={tela} className="px-4 py-2.5">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[13px] font-medium text-gray-700">{tela}</span>
                  <BadgesOrigem contagem={contarOrigens(itensTela)} />
                </div>
                {aspectos.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {aspectos.map((a) => (
                      <span key={a} className="text-[10px] text-gray-500 bg-gray-100 rounded px-1.5 py-0.5">{a}</span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, pct, cor }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
      <div className="p-3 rounded-xl" style={{ background: `${cor}1A` }}>
        <Icon className="w-5 h-5" style={{ color: cor }} />
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-800">
          {value}
          {pct != null && <span className="text-sm font-medium text-gray-400 ml-1.5">({pct}%)</span>}
        </p>
      </div>
    </div>
  );
}

export default function OrigemDadosDashboard() {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErro(null);
      try {
        setDados(await getMapeamentoOrigemDados());
      } catch (e) {
        setErro(e.message || "Acesso não autorizado.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const itens = useMemo(() => dados?.itens ?? [], [dados]);

  const { origens, totalItens, distribuicao } = useMemo(() => {
    const contagem = new Map();
    for (const item of itens) {
      const chave = item.origem_dados || "Outros";
      contagem.set(chave, (contagem.get(chave) || 0) + 1);
    }
    const total = itens.length;
    const dist = Array.from(contagem.entries()).map(([origem, qtd]) => ({
      origem,
      qtd,
      pct: total ? Math.round((qtd / total) * 1000) / 10 : 0,
    }));
    return { origens: contagem, totalItens: total, distribuicao: dist };
  }, [itens]);

  const porArea = useMemo(() => {
    const mapa = new Map();
    for (const item of itens) {
      const area = areaDoModulo(item.modulo || "");
      if (!mapa.has(area)) mapa.set(area, { area, total: 0, ...Object.fromEntries(ORIGENS.map((o) => [o, 0])) });
      const linha = mapa.get(area);
      const chave = item.origem_dados || "Outros";
      linha[chave] = (linha[chave] || 0) + 1;
      linha.total += 1;
    }
    return Array.from(mapa.values()).sort((a, b) => b.total - a.total);
  }, [itens]);

  const [areaSelecionada, setAreaSelecionada] = useState(GESTAO_ORCAMENTARIA);
  const origensPresentes = ORIGENS.filter((o) => porArea.some((l) => l[o] > 0));

  const alturaGraficoAreas = Math.max(260, porArea.length * 30);

  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>
      <SideBarNavOrigemDados isLight />

      <div className="flex flex-col flex-1 bg-gray-50" style={{ overflow: "auto" }}>
        <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center shadow-md">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin")} className="p-1.5 rounded-lg hover:bg-white/10 transition">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <span className="text-lg font-bold tracking-tight">Painel Admin · Dashboard de Origem dos Dados</span>
          </div>
          <button
            onClick={() => navigate("/inicio")}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600,
            }}
          >
            Menu Principal
          </button>
        </header>

        <main className="flex-1 px-6 py-8 max-w-6xl mx-auto w-full">
          {loading && <div className="py-16 text-center text-gray-400 text-sm">Carregando dados…</div>}
          {!loading && erro && <div className="py-16 text-center text-red-500 text-sm">{erro}</div>}

          {!loading && !erro && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                <KpiCard icon={Layers} label="Total mapeado" value={totalItens} cor="#7C3AED" />
                {ORIGENS.map((origem) => (
                  <KpiCard
                    key={origem}
                    icon={ORIGEM_ICONES[origem]}
                    label={origem}
                    value={origens.get(origem) || 0}
                    pct={totalItens ? Math.round(((origens.get(origem) || 0) / totalItens) * 100) : 0}
                    cor={ORIGEM_CORES[origem]}
                  />
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-2">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">Distribuição por origem</h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Proporção de funcionalidades do dashboard por tipo de fonte de dados.
                  </p>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={distribuicao}
                        dataKey="qtd"
                        nameKey="origem"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={2}
                      >
                        {distribuicao.map((d) => (
                          <Cell key={d.origem} fill={ORIGEM_CORES[d.origem] || COR_PADRAO} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(valor, _nome, item) => [`${valor} (${item.payload.pct}%)`, item.payload.origem]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-3 justify-center mt-2">
                    {distribuicao.map((d) => (
                      <div key={d.origem} className="flex items-center gap-1.5 text-xs text-gray-600">
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: ORIGEM_CORES[d.origem] || COR_PADRAO }} />
                        {d.origem}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-3">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">Origem por área</h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Telas agrupadas por área — clique numa barra para ver o detalhe abaixo.
                  </p>
                  <ResponsiveContainer width="100%" height={alturaGraficoAreas}>
                    <BarChart data={porArea} layout="vertical" margin={{ left: 4, right: 16 }} barCategoryGap={6}
                      onClick={(e) => e?.activePayload?.[0] && setAreaSelecionada(e.activePayload[0].payload.area)}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F1F5F9" />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} />
                      <YAxis type="category" dataKey="area" width={150} interval={0}
                        tick={({ x, y, payload }) => (
                          <text x={x - 6} y={y} dy={4} textAnchor="end" fontSize={11}
                            fill={payload.value === areaSelecionada ? "#6D28D9" : "#475569"}
                            fontWeight={payload.value === areaSelecionada ? 700 : 400}>
                            {payload.value}
                          </text>
                        )} />
                      <Tooltip content={<TooltipArea />} cursor={{ fill: "#F5F3FF" }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {origensPresentes.map((origem, idx) => (
                        <Bar key={origem} dataKey={origem} stackId="origem" fill={ORIGEM_CORES[origem]} cursor="pointer"
                          radius={idx === origensPresentes.length - 1 ? [0, 4, 4, 0] : 0}>
                          {porArea.map((linha) => (
                            <Cell key={linha.area} fillOpacity={linha.area === areaSelecionada ? 1 : 0.45} />
                          ))}
                        </Bar>
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <h3 className="text-sm font-semibold text-gray-800">Detalhe</h3>
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                  <select value={areaSelecionada} onChange={(e) => setAreaSelecionada(e.target.value)}
                    className="text-sm font-semibold text-purple-700 bg-purple-50 border border-purple-100 rounded-lg px-2 py-1 cursor-pointer">
                    {porArea.map((l) => <option key={l.area} value={l.area}>{l.area} ({l.total})</option>)}
                  </select>
                  {areaSelecionada === GESTAO_ORCAMENTARIA && (
                    <span className="text-xs text-gray-400">agrupada por tema · etiquetas cinza = partes da mesma tela</span>
                  )}
                </div>
                <DetalheArea area={areaSelecionada} itens={itens} />
              </div>
            </>
          )}
        </main>

        <footer className="text-center text-xs text-gray-400 py-6">
          © {new Date().getFullYear()} Youx Group · by financeiro
        </footer>
      </div>
    </div>
  );
}
